/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { ArrowRight2, Cancel, MicIcon, MicIcon2, StopIcon } from '../LandingHeader/Feedback/icons/Icons';
import BasicEditor from './BasicEditor';
import { LiveAudioVisualizer } from 'react-audio-visualize';
import { useSocket } from '../../SocketContext';
import { decodeToken } from "react-jwt";
import { transcribeAudio } from "../action/common";
import { getEditorContent } from "../action/utilities";

const TextAreaWithSpeech = forwardRef(({height = 'h-96', defaultValue = '', customClass = '', color = 'green', onSave = () => {}, onInput = () => {}, richText = false, placeholder = 'Speak something, and it will appear here...', minHeight = 'auto', maxHeight = 'auto'}, ref) => {
  const [text, setText] = useState(defaultValue);
  const [listening, setListening] = useState(false);
  const manuallyStopped = useRef(false);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const user = decodeToken(localStorage.getItem('token'));
  const {socket} = useSocket();
  const audioChunksRef = useRef([]);
  const [transcribing, setTranscribing] = useState(false);
  const isCanceled = useRef(false);

  useEffect(() => {
    return () => {
      cleanupAudioStream();
    }
  }, []);

  const cleanupAudioStream = async () => {
    audioChunksRef.current = []; // Discard recorded audio
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    setListening(false);
  };

  useEffect(() => {
    onInput(text);
  }, [text]);

  const stopRecording = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    setListening(false);
  }

  const cancelRecording = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    isCanceled.current = true;
    await cleanupAudioStream();
  }

  const startRecording = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Initialize audio stream
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;
    // Create and start MediaRecorder for visualizer
    mediaRecorderRef.current = new window.MediaRecorder(mediaStreamRef.current, {mimeType: 'audio/webm'});
    mediaRecorderRef.current.start();
    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0 && socket !== null) {
        audioChunksRef.current.push(event.data);
      }
    };
    // Set listening state first
    mediaRecorderRef.current.onstop = async (event) => {
      if (isCanceled.current) {
        isCanceled.current = false;
        audioChunksRef.current = [];
        return;
      }
      if (!audioChunksRef.current.length) return;

      setTranscribing(true);
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      
      try{
        const response = await transcribeAudio(formData);
        if(richText){
          const existingText = getEditorContent('description', ref);
          const newText = existingText ? existingText.replace(/<\/p>$/, ` ${response.data.responseData.transcript}</p>`) : `<p>${response.data.responseData.transcript}</p>`;
          setText(newText);
        }else{
          setText(text => text + ' ' + response.data.responseData.transcript)
        }
      }catch(err){
        console.error('Error in transcribing audio:', err);
      }
      setTranscribing(false);
      cleanupAudioStream();
    }

    setListening(true);
  }

  return (
    <>
      <div className={`self-stretch ${height} p-5 relative dark:bg-black/50 bg-white/50 rounded-[30px] shadow-[0px_4px_80px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-white/0 inline-flex justify-center items-start gap-5`} css={{
        minHeight: minHeight,
        maxHeight: maxHeight
      }}>
        <div className="h-full flex-1 justify-center dark:text-white text-black text-base font-normal font-['Inter'] leading-normal">
          <div className={`${customClass} relative w-full max-w-full`}>
            {
              richText
              ?
              <BasicEditor ref={ref} content={text} placeholder={placeholder} id="journal-editor" /> 
              :
              <textarea
                value={text}
                ref={ref}
                className={`${customClass} w-full bg-transparent dark:text-white text-black outline-none placeholder:opacity-50 resize-none`}
                onInput={(e) => setText(e.target.value)}
                placeholder={placeholder}
              />
            }
            {
              !listening && !transcribing && (
                <div className="bottom-0 right-0 absolute rounded-[50px] outline outline-1 outline-offset-[-0.50px] dark:outline-white/10 outline-black/10 flex justify-center items-center gap-2">
                    <a href="#" className="px-3.5 py-2 rounded-[50px]" title={listening ? "Stop" : "Speak"} onClick={startRecording}><MicIcon2 color={listening ? "var(--accent-color)" : "grey"} width={20} height={20} /></a>
                </div>
              )
            }
            {
              listening && (
                <div className="px-3.5 py-2 bottom-0 right-0 absolute rounded-[50px] outline outline-1 outline-offset-[-0.50px] dark:outline-white/10 outline-black/10 flex justify-center items-center gap-2 text-black/5 dark:text-white/5">
                    Recording...
                </div>
              )
            }
            {
              transcribing && (
                <div className="px-3.5 py-2 bottom-0 right-0 absolute rounded-[50px] outline outline-1 outline-offset-[-0.50px] dark:outline-white/10 outline-black/10 flex justify-center items-center gap-2 text-black/5 dark:text-white/5">
                    Transcribing...
                </div>
              )
            }
          </div>
        </div>
      </div>
      {listening && mediaRecorderRef.current && (
        <div className="self-stretch p-7 dark:bg-black/50 bg-white/50 rounded-[30px] shadow-[0px_4px_80px_0px_rgba(0,0,0,0.05)] outline outline-1 dark:outline-white/0 outline-black/0 inline-flex justify-end items-center gap-5 overflow-hidden">
          <div className="flex-1 relative opacity-50 flex justify-start items-center gap-3.5 overflow-hidden">
            <LiveAudioVisualizer
              mediaRecorder={mediaRecorderRef.current}
              width={500}
              height={100}
              barWidth={6}
              gap={10}
              barColor={color}
              maxDecibels={-30}
              minDecibels={-80}
              className="absolute bottom-0 left-0 dark:bg-black/30 bg-white/30 rounded-lg border dark:border-white/10 border-black/10"
              style={{ backdropFilter: 'blur(4px)' }}
            />
          </div>
          <div className="flex justify-start items-center gap-2.5">
              <a href="#" className="px-3.5 py-2 dark:bg-black bg-white rounded-[50px] shadow-[0px_4px_20px_0px_rgba(0,0,0,0.15)] outline outline-1 dark:outline-white/10 outline-black/10 flex justify-center items-center gap-2" title="Stop" onClick={cancelRecording}><Cancel /></a>
              <a href="#" className="px-3.5 py-2 light-background-color rounded-[50px] outline outline-1 outline-offset-[-0.50px] dark:outline-white/5 outline-black/5 flex justify-center items-center gap-2" onClick={stopRecording}css={{
                    svg:{
                        path:{
                            stroke: 'white !important'
                        }
                    }
              }}><ArrowRight2 width={20} height={20} /></a>
          </div>
        </div>
        
      )}
    </>
  );
});

export default TextAreaWithSpeech;